/**
 * QuickActionRow — a clickable row inside the "Quick actions" card. Icon + label
 * + optional sub-text on the left, chevron on the right. Renders as a full-width
 * `GlassButton` (variant=text, alignment=left) so it satisfies the 44px touch
 * floor and inherits Glass hover/focus.
 */

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, GlassButton, Typography } from '@ops-matrix/design-system-ui';
import type { ReactElement, ReactNode } from 'react';

export interface QuickActionRowProps {
  icon: ReactNode;
  label: ReactNode;
  subText?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

export function QuickActionRow({ icon, label, subText, onClick, disabled }: QuickActionRowProps): ReactElement {
  return (
    <GlassButton
      variant="text"
      onClick={onClick}
      disabled={disabled}
      sx={{
        justifyContent: 'space-between',
        textTransform: 'none',
        width: '100%',
        minHeight: 44,
        px: 1.5,
        py: 1,
        color: 'text.primary',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, textAlign: 'left' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
          {subText && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
              {subText}
            </Typography>
          )}
        </Box>
      </Box>
      <ChevronRightIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
    </GlassButton>
  );
}

export default QuickActionRow;
